import { Router } from '@angular/router';
import { GeneralService } from './services/general.service';
import { AuthService } from './services/auth.service';
import { getTenant, setAppName, getAppName, setRedirecting } from './shared/tenant.util';
import { environment } from '../environments/environment';
import { getRouteDetails } from './app-routing.module';

/**
 * Extracts tenant/appName from the URL path.
 * URL format: https://ui-sm.exostardev.com/obmui/sm_boeing_dev/...
 */
function extractTenantFromUrl(): string | null {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  
  // Find tenant - it's the segment after 'obmui'
  const obmuiIndex = pathSegments.findIndex(s => s.toLowerCase() === 'obmui');
  if (obmuiIndex !== -1 && pathSegments.length > obmuiIndex + 1) {
    const tenant = pathSegments[obmuiIndex + 1];
    if (environment.tenants[tenant.toLowerCase()]) {
      return tenant.toLowerCase();
    }
  }
  
  // Fallback: first segment if it's a known tenant
  if (pathSegments.length > 0 && environment.tenants[pathSegments[0].toLowerCase()]) {
    return pathSegments[0].toLowerCase();
  }
  
  return null;
}

/**
 * Redirects to MAG login page.
 * Used when user enters base URL without tenant and is not authenticated.
 */
function redirectToMagLogin(): void {
  const magLoginUrl = (environment as any).magLoginUrl;
  if (magLoginUrl) {
    console.log('[APP_INIT] Redirecting to MAG login:', magLoginUrl);
    setRedirecting(true); // Set flag before redirect
    window.location.href = magLoginUrl;
  } else {
    console.error('[APP_INIT] magLoginUrl not configured in environment');
    alert('Configuration error: MAG login URL not configured. Please contact administrator.');
  }
}

export function initializeApp(generalService: GeneralService, authService: AuthService, router: Router) {
  return async (): Promise<void> => {
    console.log('[APP_INIT] Starting app initialization...');
    console.log('[APP_INIT] Current URL:', window.location.href);
    
    // Set redirecting flag at start - will be cleared only if fully authenticated
    // This prevents any flash of error pages during initialization
    setRedirecting(true);
    
    // Step 1: Extract tenant from URL and store it
    const tenantFromUrl = extractTenantFromUrl();
    if (tenantFromUrl) {
      setAppName(tenantFromUrl);
      console.log('[APP_INIT] Tenant extracted from URL:', tenantFromUrl);
    }
    
    const backendUrl = getTenant();
    console.log('[APP_INIT] Backend URL:', backendUrl);
    
    // Step 2: Handle case when NO tenant in URL (user entered base URL only)
    if (!backendUrl) {
      console.log('[APP_INIT] No tenant found in URL');
      
      // Check if user has a previously stored tenant/appName
      const storedAppName = getAppName();
      if (storedAppName && environment.tenants[storedAppName]) {
        console.log('[APP_INIT] Found stored tenant:', storedAppName);
        // Redirect to the stored tenant URL
        const redirectUrl = `${window.location.origin}/obmui/${storedAppName}/`;
        console.log('[APP_INIT] Redirecting to stored tenant URL:', redirectUrl);
        window.location.href = redirectUrl;
        return;
      }
      
      // No tenant in URL and no stored tenant
      // Redirect to MAG login page so user can select their application
      console.log('[APP_INIT] No tenant found, redirecting to MAG login');
      redirectToMagLogin();
      return;
    }

    let token = authService.getToken();

    if (!token) {
      console.log('[APP_INIT] No token found, checking authentication...');

      // Branch on per-tenant auth mode EXACTLY as authGuard does. Previously this
      // always ran the credentialed checkBackendAuth() (token 'WS_FED_SESSION',
      // requests sent withCredentials), regardless of tenantAuthMode. For a
      // MAGBypass tenant the guard instead uses the NON-credentialed
      // checkMagBypass() (token 'MAG_BYPASS_ENABLED'), so the token type — and
      // therefore whether the Accept POST / status GET carry credentials —
      // depended on which path won the race on a given load. That inconsistency
      // made acceptance flaky across reloads (write and read resolving against
      // different session contexts). Keeping both paths identical fixes it.
      const appName = (getAppName() || '').toLowerCase();
      const mode = (environment as any).tenantAuthMode?.[appName] ?? 'wsfed';

      if (mode === 'magbypass') {
        const isBypass = await authService.checkMagBypass();
        if (isBypass) {
          console.log('[APP_INIT] MAGBypass active for tenant:', appName);
          setRedirecting(false);
        } else {
          console.warn('[APP_INIT] MAGBypass backend not reachable - auth guard will handle');
          return;
        }
      } else {
        // WS-Federation (cutover tenants, e.g. sem1)
        const isAuthenticated = await authService.checkBackendAuth();
        if (isAuthenticated) {
          console.log('[APP_INIT] User authenticated via WS-Fed');
          // User is authenticated - clear the redirecting flag so app renders
          setRedirecting(false);
        } else {
          console.log('[APP_INIT] Not authenticated - auth guard will handle WS-Fed redirect');
          // Keep redirecting flag true - auth guard will redirect to WS-Fed
          return;
        }
      }
    } else {
      // User already has token - clear redirecting flag
      setRedirecting(false);
    }

    await generalService.loadConfig().toPromise();
    console.log('[APP_INIT] Config loaded successfully');

    // The static route table (RouterModule.forRoot(getRouteDetails())) is built at
    // bootstrap, BEFORE loadConfig()/checkBackendAuth() store 'loggedInUserDetails'.
    // On first login that means getRoleContext() falls back to its default (Buyer), so
    // role-specific routes such as '/supplier-home' are missing for LMC/SEM (Supplier)
    // users — and the post-Accept redirect on the T&C page hits the '**' wildcard and
    // bounces back to /terms-and-condition until a full refresh (SM-3608). Now that the
    // real role is known, rebuild the route table so those routes exist on the first
    // navigation (no manual reload needed).
    router.resetConfig(getRouteDetails());
    console.log('[APP_INIT] Route table rebuilt for role');
  };
}

